import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin, of, switchMap } from 'rxjs';

import { environment } from '../environments/environment';
import { CheckoutRecord, Reservation } from '../core/models/equipment.models';
import {
  EquipmentApiResult,
  EquipmentService,
} from '../core/services/equipment.service';
import { ExportService } from '../core/services/export.service';
import { InventoryService } from '../core/services/inventory.service';
import { SettingsService } from '../core/services/settings.service';
import { confirmAction } from '../core/utils/confirm.util';
import {
  defaultBranchFilter,
  defaultBranchFilterId,
  isBranchFilterLocked,
  matchesBranchFilter,
} from '../core/utils/branch-scope.util';
import { reservationStatusLabel } from '../core/utils/equipment-rental.util';
import { AuthService } from '../modules/auth/auth.service';
import { currentTimestamp, formatDate } from '../core/utils/format.util';

interface ReservationListRow extends Reservation {
  memberIds: string[];
  memberNumbers: string[];
}

interface ReservationHistoryRow extends ReservationListRow {
  outcome: 'checked-out' | 'returned' | 'cancelled';
  events: string[];
}

interface ReservedUnit {
  key: string;
  reservationId: string;
  label: string;
  serialNumber: string;
  mode: 'member' | 'quantity';
}
import { matchesItemQuery } from '../core/utils/item-filter.util';

interface ReservationPick {
  key: string;
  inventoryItemID: number;
  name: string;
  code: string;
  serialNumber: string;
  equipmentAssetId: number;
}

@Component({
  selector: 'app-reservations',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './reservations.component.html',
  styleUrls: ['./reservations.component.css'],
})
export class ReservationsComponent implements OnInit {
  private readonly equipmentService = inject(EquipmentService);
  private readonly inventory = inject(InventoryService);
  private readonly settings = inject(SettingsService);
  private readonly exporter = inject(ExportService);
  protected readonly auth = inject(AuthService);

  readonly timestamp = currentTimestamp;
  readonly formatDate = formatDate;
  readonly reservationStatusLabel = reservationStatusLabel;
  readonly allReservations = this.equipmentService.allReservations;
  readonly allAssets = this.equipmentService.allAssets;
  readonly branches = this.settings.scopedBranches;
  readonly locations = this.settings.scopedLocations;
  readonly branchFilterLocked = computed(() => isBranchFilterLocked(this.auth));

  // ============================================
  // STATE
  // ============================================
  readonly searchQuery = signal('');
  readonly listView = signal<'current' | 'history'>('current');
  readonly statusFilter = signal('');
  readonly branchFilter = signal('all');
  readonly locationFilter = signal('all');

  readonly showReservationModal = signal(false);
  readonly editingId = signal<string | null>(null);
  /** Original row — used so a status change does not rewrite equipment/location from a bad refresh. */
  readonly editingSnapshot = signal<Reservation | null>(null);
  readonly isEditing = computed(() => this.editingId() !== null);

  readonly formCustomer = signal('');
  readonly formEquipment = signal('');
  readonly serialOptions = signal<Array<{ id: string; serialNumber: string }>>([]);
  readonly serialsLoading = signal(false);
  readonly selectedSerial = signal('');
  readonly formBranch = signal('');
  readonly formLocation = signal('');
  readonly formStartDate = signal('');
  readonly formEndDate = signal('');
  readonly formStatus = signal<Reservation['status']>('pending');
  readonly reservationPicks = signal<ReservationPick[]>([]);
  readonly editUnits = signal<ReservedUnit[]>([]);

  readonly showToast = signal(false);
  readonly toastMessage = signal('');

  constructor() {
    effect(() => {
      const branches = this.branches();
      if (!branches.length) {
        return;
      }
      this.branchFilter.set(defaultBranchFilter(this.auth, branches));
    });
  }

  // ============================================
  // COMPUTED
  // ============================================
  readonly equipmentLoading = signal(false);
  readonly equipmentNameFilter = signal('');

  private reservationBranchId(): string {
    return (
      this.formBranch() ||
      (this.branchFilterLocked()
        ? defaultBranchFilterId(this.auth, this.branches())
        : '')
    );
  }

  /** Free units of this item at the selected branch, or at the store location when one is chosen. */
  availableCount(inventoryItemId: number): number {
    if (!inventoryItemId) {
      return 0;
    }
    const branchId = this.reservationBranchId();
    const locationId = this.formLocation();
    const location = this.locations().find((row) => row.id === locationId);
    const stock = locationId
      ? this.inventory.availableQuantityAtLocation(
          inventoryItemId,
          branchId || undefined,
          locationId,
          location?.name,
        )
      : this.inventory.availableQuantityAtBranch(
          inventoryItemId,
          branchId || undefined,
        );
    const tiedUp = this.equipmentService.unavailableSerialCount(
      inventoryItemId,
      branchId || undefined,
      locationId || undefined,
      location?.name,
    );
    return Math.max(0, stock - tiedUp);
  }

  readonly reservableOptions = computed(() => {
    const query = this.equipmentNameFilter();
    const selectedId = String(this.formEquipment() ?? '');
    return this.inventory
      .allItems()
      .filter((item) => this.inventory.isEligibleForEquipmentRental(item))
      .map((item) => {
        const inventoryItemID = Number(item.id) || 0;
        return {
          id: `inv-${item.id}`,
          inventoryItemID,
          name: item.name,
          code: item.code,
          available: this.availableCount(inventoryItemID),
        };
      })
      .filter(
        (row) =>
          row.available > 0 &&
          (row.id === selectedId ||
            matchesItemQuery(query, row.name, row.code)),
      );
  });

  readonly availabilityNotice = computed(() => {
    const query = this.equipmentNameFilter().trim();
    const place = this.formLocation()
      ? 'at this store location'
      : 'at this branch';
    const rentable = this.inventory
      .allItems()
      .filter((item) => this.inventory.isEligibleForEquipmentRental(item));
    const pool = query
      ? rentable.filter((item) => matchesItemQuery(query, item.name, item.code))
      : rentable;
    if (!pool.length) {
      return '';
    }
    const anyAvailable = pool.some(
      (item) => this.availableCount(Number(item.id) || 0) > 0,
    );
    if (anyAvailable) {
      return '';
    }
    if (query && pool.length === 1) {
      return `${pool[0].name} is not available to reserve ${place}.`;
    }
    if (query) {
      return `No matching items are available to reserve ${place}.`;
    }
    return `No items are available to reserve ${place}.`;
  });

  readonly selectedReservable = computed(() => {
    const selectedId = String(this.formEquipment() ?? '');
    if (!selectedId) {
      return undefined;
    }
    return this.reservableOptions().find((row) => row.id === selectedId);
  });

  /** Free units of the selected item name on Current Stock. Same figure as the item list. */
  readonly selectedItemAvailable = computed(
    () => this.selectedReservable()?.available ?? 0,
  );

  readonly formBranchLocations = computed(() => {
    const branchId = this.formBranch();
    if (!branchId) {
      return this.locations();
    }
    return this.locations().filter(
      (location) => location.branchId === branchId,
    );
  });

  readonly reservationStats = computed(() => {
    const list = this.groupReservations(this.allReservations());
    return {
      total: list.length,
      readyToIssue: list.filter((row) => row.status === 'completed').length,
      issuedOut: list.filter((row) => row.status === 'issued').length,
      confirmed: list.filter((row) => row.status === 'confirmed').length,
      pending: list.filter((row) => row.status === 'pending').length,
      cancelled: list.filter((row) => row.status === 'cancelled').length,
    };
  });

  readonly filteredReservations = computed(() => {
    const search = this.searchQuery().trim().toLowerCase();
    const status = this.statusFilter();
    const branch = this.branchFilter();
    const location = this.locationFilter();

    const matched = this.allReservations().filter((reservation) => {
      if (
        reservation.status !== 'pending' &&
        reservation.status !== 'confirmed' &&
        reservation.status !== 'completed'
      ) {
        return false;
      }
      if (status && reservation.status !== status) {
        return false;
      }
      if (
        !matchesBranchFilter(
          branch,
          reservation.branch,
          reservation.branchId,
          this.branches(),
        )
      ) {
        return false;
      }
      if (location !== 'all' && reservation.location !== location) {
        return false;
      }
      return true;
    });

    return this.groupReservations(matched).filter((reservation) =>
      this.matchesReservationSearch(reservation, search),
    );
  });

  /** Issued, returned, and cancelled bookings, oldest first. */
  readonly historyReservations = computed(() => {
    const search = this.searchQuery().trim().toLowerCase();
    const outcome = this.statusFilter();
    const branch = this.branchFilter();
    const location = this.locationFilter();
    const checkouts = this.equipmentService.allCheckouts();

    return this.groupReservations(this.allReservations())
      .filter((reservation) => {
        if (
          !matchesBranchFilter(
            branch,
            reservation.branch,
            reservation.branchId,
            this.branches(),
          )
        ) {
          return false;
        }
        if (location !== 'all' && reservation.location !== location) {
          return false;
        }
        return this.matchesReservationSearch(reservation, search);
      })
      .map((reservation) =>
        this.toHistoryRow(
          reservation,
          this.linkedCheckouts(reservation, checkouts),
        ),
      )
      .filter((row): row is ReservationHistoryRow => row !== null)
      .filter((row) => !outcome || row.outcome === outcome);
  });

  showCurrentList(): void {
    this.listView.set('current');
    this.statusFilter.set('');
  }

  showHistoryList(): void {
    this.listView.set('history');
    this.statusFilter.set('');
  }

  private matchesReservationSearch(
    reservation: ReservationListRow,
    search: string,
  ): boolean {
    if (!search) {
      return true;
    }
    const numbers = reservation.memberNumbers.join(' ').toLowerCase();
    return (
      numbers.includes(search) ||
      reservation.customer.toLowerCase().includes(search) ||
      reservation.equipment.toLowerCase().includes(search) ||
      (reservation.branch ?? '').toLowerCase().includes(search) ||
      (reservation.location ?? '').toLowerCase().includes(search)
    );
  }

  private linkedCheckouts(
    reservation: ReservationListRow,
    checkouts: readonly CheckoutRecord[],
  ): CheckoutRecord[] {
    const ids = new Set(reservation.memberIds);
    const numbers = new Set(
      reservation.memberNumbers.map((number) => number.toLowerCase()),
    );
    return checkouts.filter(
      (checkout) =>
        (!!checkout.reservationId && ids.has(checkout.reservationId)) ||
        (!!checkout.reservationNumber &&
          numbers.has(checkout.reservationNumber.toLowerCase())),
    );
  }

  private toHistoryRow(
    reservation: ReservationListRow,
    checkouts: CheckoutRecord[],
  ): ReservationHistoryRow | null {
    const issued = reservation.status === 'issued';
    const cancelled = reservation.status === 'cancelled';
    if (!issued && !cancelled) {
      return null;
    }

    const checkoutDates = checkouts
      .map((checkout) => checkout.checkoutDate)
      .filter((date) => !!date)
      .sort();
    const fullyReturned =
      checkouts.length > 0 &&
      checkouts.every((checkout) => (checkout.quantityOutstanding ?? 1) === 0);
    const partlyReturned = checkouts.some(
      (checkout) => (checkout.quantityReturned ?? 0) > 0 && !fullyReturned,
    );

    const events = [`Booked ${formatDate(reservation.startDate)}`];
    if (issued) {
      events.push(
        checkoutDates.length
          ? `Checked out ${formatDate(checkoutDates[0])}`
          : 'Checked out',
      );
    }
    if (fullyReturned) {
      events.push('Returned');
    } else if (partlyReturned) {
      events.push('Partly returned');
    }
    if (cancelled) {
      events.push('Cancelled');
    }

    const outcome: ReservationHistoryRow['outcome'] = cancelled
      ? 'cancelled'
      : fullyReturned
        ? 'returned'
        : 'checked-out';

    return { ...reservation, outcome, events };
  }

  // ============================================
  // METHODS
  // ============================================
  private reservationCreatedAt(reservation: Reservation): number | null {
    const match = reservation.reservationNumber.match(
      /RES-(\d{8})-(\d{6})-/i,
    );
    if (!match) {
      return null;
    }
    const day = match[1];
    const time = match[2];
    const parsed = Date.parse(
      `${day.slice(0, 4)}-${day.slice(4, 6)}-${day.slice(6, 8)}T${time.slice(0, 2)}:${time.slice(2, 4)}:${time.slice(4, 6)}`,
    );
    return Number.isNaN(parsed) ? null : parsed;
  }

  private sameReservationBatch(left: Reservation, right: Reservation): boolean {
    if (left.status !== right.status) {
      return false;
    }
    if (
      left.customer.trim().toLowerCase() !== right.customer.trim().toLowerCase()
    ) {
      return false;
    }
    const leftItem = left.inventoryItemID || left.equipment.trim().toLowerCase();
    const rightItem =
      right.inventoryItemID || right.equipment.trim().toLowerCase();
    if (leftItem !== rightItem) {
      return false;
    }
    if ((left.branchId || left.branch || '') !== (right.branchId || right.branch || '')) {
      return false;
    }
    if (
      (left.locationId || left.location || '') !==
      (right.locationId || right.location || '')
    ) {
      return false;
    }
    if (left.startDate !== right.startDate || left.endDate !== right.endDate) {
      return false;
    }
    const leftAt = this.reservationCreatedAt(left);
    const rightAt = this.reservationCreatedAt(right);
    if (leftAt == null || rightAt == null) {
      return false;
    }
    return Math.abs(rightAt - leftAt) <= 120_000;
  }

  private groupReservations(rows: Reservation[]): ReservationListRow[] {
    const buckets = new Map<string, Reservation[]>();
    for (const row of rows) {
      const key = [
        row.customer.trim().toLowerCase(),
        row.inventoryItemID || row.equipment.trim().toLowerCase(),
        row.branchId || row.branch || '',
        row.locationId || row.location || '',
        row.startDate,
        row.endDate,
        row.status,
      ].join('|');
      const bucket = buckets.get(key) ?? [];
      bucket.push(row);
      buckets.set(key, bucket);
    }

    const grouped: ReservationListRow[] = [];
    for (const bucket of buckets.values()) {
      const sorted = [...bucket].sort((left, right) => {
        const leftAt = this.reservationCreatedAt(left) ?? 0;
        const rightAt = this.reservationCreatedAt(right) ?? 0;
        return leftAt - rightAt;
      });
      let current: Reservation[] = [];
      for (const row of sorted) {
        const previous = current[current.length - 1];
        if (previous && this.sameReservationBatch(previous, row)) {
          current.push(row);
          continue;
        }
        if (current.length) {
          grouped.push(this.toReservationListRow(current));
        }
        current = [row];
      }
      if (current.length) {
        grouped.push(this.toReservationListRow(current));
      }
    }

    return grouped.sort((left, right) => {
      const byDate = (left.startDate || '').localeCompare(right.startDate || '');
      if (byDate !== 0) {
        return byDate;
      }
      return left.reservationNumber.localeCompare(right.reservationNumber);
    });
  }

  private toReservationListRow(members: Reservation[]): ReservationListRow {
    const first = members[0];
    return {
      ...first,
      quantityRequested: members.reduce(
        (sum, row) => sum + (row.quantityRequested ?? 1),
        0,
      ),
      memberIds: members.map((row) => row.id),
      memberNumbers: members.map((row) => row.reservationNumber),
    };
  }

  private reservationMembers(reservation: ReservationListRow): Reservation[] {
    const ids = new Set(reservation.memberIds ?? [reservation.id]);
    const members = this.allReservations().filter((row) => ids.has(row.id));
    return members.length ? members : [reservation];
  }

  ngOnInit(): void {
    if (!environment.useMockData) {
      this.settings.refreshFromApi();
      this.inventory.loadItemMasterAndStock().subscribe(() => {
        this.equipmentService.refreshEquipmentAssetsFromApi().subscribe();
        this.equipmentService.refreshCheckoutQueueFromApi().subscribe();
      });
    }
  }

  findAssetIcon(equipmentName: string): string {
    const asset = this.allAssets().find((row) => row.name === equipmentName);
    return asset?.icon ?? 'bi-box-seam';
  }

  openReservationModal(): void {
    const today = new Date().toISOString().slice(0, 10);
    const defaultBranch = defaultBranchFilterId(this.auth, this.branches());
    const branchId =
      defaultBranch !== 'all' ? defaultBranch : (this.branches()[0]?.id ?? '');

    this.editingId.set(null);
    this.equipmentNameFilter.set('');
    this.formCustomer.set('');
    this.formEquipment.set('');
    this.formBranch.set(branchId);
    this.formLocation.set('');
    this.formStartDate.set(today);
    this.formEndDate.set(today);
    this.formStatus.set('pending');
    this.reservationPicks.set([]);
    this.selectedSerial.set('');
    this.serialOptions.set([]);
    this.showReservationModal.set(true);
    document.body.style.overflow = 'hidden';

    if (!environment.useMockData) {
      this.equipmentLoading.set(true);
      this.settings.refreshFromApi();
      this.inventory.loadItemMasterAndStock().subscribe(() => {
        this.equipmentService.refreshEquipmentAssetsFromApi().subscribe(() => {
          this.equipmentLoading.set(false);
        });
      });
      return;
    }
  }

  equipmentOptionId(asset: { id: string | number }): string {
    return String(asset.id);
  }

  onFormEquipmentChange(equipmentId: string): void {
    const id = String(equipmentId ?? '');
    this.formEquipment.set(id);
    this.selectedSerial.set('');
    this.serialOptions.set([]);
    if (!id) {
      this.serialsLoading.set(false);
      return;
    }
    const option = this.reservableOptions().find((row) => row.id === id);
    if (!option || option.available <= 0) {
      this.formEquipment.set('');
      this.showToastMessage(
        option
          ? `${option.name} is not available to reserve.`
          : 'This item is not available to reserve.',
        'info',
      );
      return;
    }
    this.loadReservationSerials(id, option.inventoryItemID);
  }

  private loadReservationSerials(
    equipmentId: string,
    inventoryItemID: number,
  ): void {
    if (!inventoryItemID) {
      this.serialsLoading.set(false);
      return;
    }
    this.serialsLoading.set(true);
    this.inventory.fetchItemHistory(inventoryItemID).subscribe({
      next: (result) => {
        if (this.formEquipment() !== equipmentId) {
          return;
        }
        const blocked = new Set([
          'ISSUED',
          'CHECKED_OUT',
          'CHECKED OUT',
          'IN_REPAIR',
          'IN REPAIR',
          'IN_TRANSIT',
          'IN TRANSIT',
          'RESERVED',
          'DAMAGED',
        ]);
        const options = (result.data?.assets ?? []).flatMap((row) => {
          const serial = (row.serialNumber || '').trim();
          if (!serial || /^AST-\d+/i.test(serial)) {
            return [];
          }
          const status = (row.status || 'AVAILABLE').trim().toUpperCase();
          if (blocked.has(status)) {
            return [];
          }
          return [{ id: row.id, serialNumber: serial }];
        });
        this.serialOptions.set(options);
        this.serialsLoading.set(false);
        if (
          this.availableCount(inventoryItemID) <= 0 &&
          options.length === 0
        ) {
          this.formEquipment.set('');
          const name =
            this.inventory.findItemById(inventoryItemID)?.name ?? 'This item';
          this.showToastMessage(
            `${name} is not available to reserve.`,
            'info',
          );
        }
      },
      error: () => this.serialsLoading.set(false),
    });
  }

  addReservationItem(): void {
    const option = this.selectedReservable();
    if (!option) {
      this.showToastMessage('Select an available item.', 'error');
      return;
    }
    if (option.available <= 0) {
      this.showToastMessage(
        `${option.name} is not available to reserve.`,
        'info',
      );
      return;
    }
    if (this.serialsLoading()) {
      this.showToastMessage('Serial numbers are still loading.', 'info');
      return;
    }

    const picks = this.reservationPicks();
    const serials = this.serialOptions();
    if (serials.length) {
      const serialNumber = this.selectedSerial().trim();
      if (!serialNumber) {
        this.showToastMessage(
          'Select an available serial number for this item.',
          'error',
        );
        return;
      }
      const serial = serials.find((row) => row.serialNumber === serialNumber);
      if (!serial) {
        this.showToastMessage(
          'That serial number is not available to reserve.',
          'info',
        );
        return;
      }
      if (picks.some((row) => row.serialNumber === serialNumber)) {
        this.showToastMessage(
          `${serialNumber} is already on this reservation.`,
          'info',
        );
        return;
      }
      const sameName = picks.filter(
        (row) => row.inventoryItemID === option.inventoryItemID,
      ).length;
      if (sameName >= serials.length) {
        this.showToastMessage(
          `Only ${serials.length} ${option.name} available to reserve.`,
          'info',
        );
        return;
      }
      this.reservationPicks.set([
        ...picks,
        {
          key: `${option.inventoryItemID}-${serial.id}`,
          inventoryItemID: option.inventoryItemID,
          name: option.name,
          code: option.code,
          serialNumber,
          equipmentAssetId: Number(serial.id) || 0,
        },
      ]);
      this.selectedSerial.set('');
      return;
    }

    const already = picks.filter(
      (row) => row.inventoryItemID === option.inventoryItemID,
    ).length;
    if (already >= option.available) {
      this.showToastMessage(
        option.available <= 0
          ? `${option.name} is not available to reserve.`
          : `Only ${option.available} ${option.name} available to reserve.`,
        'info',
      );
      return;
    }

    this.reservationPicks.set([
      ...picks,
      {
        key: `${option.inventoryItemID}-${already + 1}`,
        inventoryItemID: option.inventoryItemID,
        name: option.name,
        code: option.code,
        serialNumber: '',
        equipmentAssetId: 0,
      },
    ]);
  }

  removeReservationPick(key: string): void {
    this.reservationPicks.update((rows) =>
      rows.filter((row) => row.key !== key),
    );
  }

  pickAvailableCount(pick: ReservationPick): number {
    return this.availableCount(pick.inventoryItemID);
  }

  onFormBranchChange(branchId: string): void {
    this.formBranch.set(branchId);
    const locations = this.locations().filter(
      (location) => location.branchId === branchId,
    );
    const currentLocation = this.formLocation();
    if (!locations.some((location) => location.id === currentLocation)) {
      this.formLocation.set('');
    }
    this.keepOnlyAvailableSelection();
  }

  onFormLocationChange(locationId: string): void {
    this.formLocation.set(locationId);
    this.keepOnlyAvailableSelection();
  }

  private keepOnlyAvailableSelection(): void {
    const picks = this.reservationPicks();
    const kept: ReservationPick[] = [];
    const removedNames: string[] = [];
    const used = new Map<number, number>();
    for (const pick of picks) {
      const available = this.availableCount(pick.inventoryItemID);
      const count = used.get(pick.inventoryItemID) ?? 0;
      if (available <= 0 || count + 1 > available) {
        removedNames.push(pick.name);
        continue;
      }
      used.set(pick.inventoryItemID, count + 1);
      kept.push(pick);
    }
    if (removedNames.length) {
      this.reservationPicks.set(kept);
      const names = [...new Set(removedNames)];
      this.showToastMessage(
        names.length === 1
          ? `${names[0]} is not available to reserve.`
          : `${names.join(', ')} are not available to reserve.`,
        'info',
      );
    }

    const current = String(this.formEquipment() ?? '');
    if (current && !this.reservableOptions().some((row) => row.id === current)) {
      const name = this.selectedItemName(current);
      this.formEquipment.set('');
      this.selectedSerial.set('');
      this.serialOptions.set([]);
      if (name) {
        this.showToastMessage(`${name} is not available to reserve.`, 'info');
      }
    }

    const notice = this.availabilityNotice();
    if (!this.equipmentNameFilter().trim() && notice) {
      this.showToastMessage(notice, 'info');
    }
  }

  private selectedItemName(optionId: string): string {
    const id = optionId.startsWith('inv-') ? optionId.slice(4) : optionId;
    return this.inventory.findItemById(id)?.name ?? '';
  }

  equipmentOptionLabel(asset: {
    code: string;
    name: string;
    available: number;
  }): string {
    const code = asset.code ? `${asset.code} — ` : '';
    return `${code}${asset.name} (${asset.available} available)`;
  }

  reservationIsIssued(reservation: Reservation | null | undefined): boolean {
    if (!reservation) {
      return false;
    }
    return (
      reservation.status === 'issued' ||
      this.equipmentService.isReservationCheckedOut(reservation)
    );
  }

  canRemoveReservedItems(
    reservation: Reservation | null | undefined = this.editingSnapshot(),
  ): boolean {
    return (
      reservation?.status === 'pending' || reservation?.status === 'confirmed'
    );
  }

  removeEditUnit(key: string): void {
    if (!this.canRemoveReservedItems()) {
      this.showToastMessage(
        'Items can only be removed while the reservation is still pending or confirmed.',
        'error',
      );
      return;
    }
    if (this.editUnits().length <= 1) {
      this.showToastMessage(
        'Leave at least one item, or cancel the whole reservation.',
        'info',
      );
      return;
    }
    this.editUnits.update((rows) => rows.filter((row) => row.key !== key));
  }

  private serialForReservation(member: Reservation): string {
    const asset = this.allAssets().find((row) => {
      if (
        member.equipmentAssetID &&
        Number(row.id) === member.equipmentAssetID
      ) {
        return true;
      }
      return row.reservationId === member.id;
    });
    const serial = (asset?.serialNumber ?? '').trim();
    if (!serial || /^AST-\d+/i.test(serial)) {
      return '';
    }
    return serial;
  }

  private buildEditUnits(reservation: ReservationListRow): ReservedUnit[] {
    const members = this.reservationMembers(reservation);
    const units: ReservedUnit[] = [];
    for (const member of members) {
      const serial = this.serialForReservation(member);
      const quantity = Math.max(1, member.quantityRequested ?? 1);
      if (member.equipmentAssetID || serial || quantity <= 1) {
        units.push({
          key: member.id,
          reservationId: member.id,
          label: member.equipment,
          serialNumber: serial,
          mode: 'member',
        });
        continue;
      }
      for (let index = 0; index < quantity; index += 1) {
        units.push({
          key: `${member.id}-${index}`,
          reservationId: member.id,
          label: member.equipment,
          serialNumber: '',
          mode: 'quantity',
        });
      }
    }
    return units;
  }

  openEditModal(reservation: ReservationListRow): void {
    this.editingSnapshot.set(reservation);
    this.editingId.set(reservation.id);
    this.editUnits.set(this.buildEditUnits(reservation));
    this.formCustomer.set(reservation.customer);
    this.formEquipment.set('');
    this.formBranch.set(reservation.branchId ?? '');
    this.formLocation.set(reservation.locationId ?? '');
    this.formStartDate.set(reservation.startDate);
    this.formEndDate.set(reservation.endDate);
    this.formStatus.set(reservation.status);
    this.showReservationModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeReservationModal(): void {
    this.showReservationModal.set(false);
    this.editingId.set(null);
    this.editingSnapshot.set(null);
    this.editUnits.set([]);
    document.body.style.overflow = 'auto';
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeReservationModal();
    }
  }

  saveReservation(): void {
    const editingId = this.editingId();
    const snapshot = this.editingSnapshot();

    if (editingId && snapshot) {
      if (this.reservationIsIssued(snapshot) && this.formStatus() !== snapshot.status) {
        this.showToastMessage(
          'This item has been issued. The reservation status cannot be changed.',
          'error',
        );
        this.formStatus.set(snapshot.status);
        return;
      }
      const status = this.reservationIsIssued(snapshot)
        ? snapshot.status
        : this.formStatus();
      const startDate = this.formStartDate();
      const endDate = this.formEndDate();
      if (!startDate || !endDate) {
        this.showToastMessage('Start and end dates are required', 'error');
        return;
      }
      const members = this.reservationMembers(snapshot as ReservationListRow);
      const units = this.editUnits();
      if (this.canRemoveReservedItems(snapshot) && !units.length) {
        this.showToastMessage(
          'Leave at least one item, or cancel the whole reservation.',
          'error',
        );
        return;
      }
      const removed = this.canRemoveReservedItems(snapshot)
        ? members.filter(
            (member) =>
              !units.some((unit) => unit.reservationId === member.id),
          )
        : [];
      const quantityChanges: Array<{ member: Reservation; quantity: number }> =
        this.canRemoveReservedItems(snapshot)
          ? members.flatMap((member) => {
              const kept = units.filter(
                (unit) =>
                  unit.reservationId === member.id && unit.mode === 'quantity',
              ).length;
              const original = Math.max(1, member.quantityRequested ?? 1);
              if (kept > 0 && kept < original) {
                return [{ member, quantity: kept }];
              }
              return [];
            })
          : [];
      const kept = members.filter((member) =>
        units.some((unit) => unit.reservationId === member.id),
      );
      const removals = removed.length
        ? forkJoin(
            removed.map((member) =>
              this.equipmentService.deleteReservationViaApi(member.id),
            ),
          )
        : of([] as EquipmentApiResult[]);

      removals
        .pipe(
          switchMap((removedResults) => {
            const failed = removedResults.find((row) => !row.success);
            if (failed) {
              return of([failed]);
            }
            if (!kept.length) {
              return of([{ success: true }] as EquipmentApiResult[]);
            }
            return forkJoin(
              kept.map((member) => {
                const nextQuantity = quantityChanges.find(
                  (change) => change.member.id === member.id,
                )?.quantity;
                return this.equipmentService.updateReservationViaApi(member.id, {
                  status,
                  startDate,
                  endDate,
                  quantityRequested: nextQuantity,
                  preserveDisplay: {
                    customer: member.customer,
                    equipment: member.equipment,
                    branch: member.branch,
                    branchId: member.branchId,
                    location: member.location,
                    locationId: member.locationId,
                    quantityRequested: nextQuantity ?? member.quantityRequested,
                    inventoryItemID: member.inventoryItemID,
                    equipmentAssetID: member.equipmentAssetID,
                  },
                });
              }),
            );
          }),
        )
        .subscribe((results) => {
          const result = results.find((row) => !row.success) ?? results[0];
          if (result?.success) {
            const dropped =
              removed.length +
              quantityChanges.reduce(
                (sum, change) =>
                  sum +
                  Math.max(1, change.member.quantityRequested ?? 1) -
                  change.quantity,
                0,
              );
            this.closeReservationModal();
            if (dropped > 0) {
              this.showToastMessage(
                `Removed ${dropped} item(s). ${units.length} still reserved.`,
                'success',
              );
            } else if (status === 'completed') {
              this.showToastMessage(
                'Reservation completed. Equipment is paid and waiting on Check-Out.',
                'success',
              );
            } else if (status === 'cancelled') {
              this.showToastMessage(
                'Completed reservation cancelled. Waiting checkout removed; reservation is Pending again.',
                'success',
              );
            } else {
              this.showToastMessage(
                'Reservation updated successfully',
                'success',
              );
            }
          } else {
            this.showToastMessage(
              (result && 'message' in result ? result.message : undefined) ??
                'Failed to update reservation',
              'error',
            );
          }
        });
      return;
    }

    const customer = this.formCustomer().trim();
    const branchId = this.formBranch();
    const locationId = this.formLocation();
    const startDate = this.formStartDate();
    const endDate = this.formEndDate();
    const picks = this.reservationPicks();

    if (
      !customer ||
      !picks.length ||
      !startDate ||
      !endDate ||
      !branchId ||
      !locationId
    ) {
      this.showToastMessage(
        picks.length
          ? 'Please fill in all required fields'
          : 'Add at least one available item.',
        'error',
      );
      return;
    }

    const over = new Map<number, { name: string; count: number }>();
    for (const pick of picks) {
      const row = over.get(pick.inventoryItemID) ?? {
        name: pick.name,
        count: 0,
      };
      row.count += 1;
      over.set(pick.inventoryItemID, row);
    }
    for (const [itemId, row] of over) {
      const available = this.availableCount(itemId);
      if (row.count > available) {
        this.showToastMessage(
          available <= 0
            ? `${row.name} is not available to reserve.`
            : `Only ${available} ${row.name} available to reserve.`,
          'info',
        );
        return;
      }
    }

    const lines: Array<{
      inventoryItemID: number;
      name: string;
      equipmentAssetID: number;
      quantity: number;
    }> = [];
    const quantityCounts = new Map<number, { name: string; count: number }>();
    for (const pick of picks) {
      if (pick.equipmentAssetId > 0) {
        lines.push({
          inventoryItemID: pick.inventoryItemID,
          name: pick.serialNumber
            ? `${pick.name} (${pick.serialNumber})`
            : pick.name,
          equipmentAssetID: pick.equipmentAssetId,
          quantity: 1,
        });
        continue;
      }
      const row = quantityCounts.get(pick.inventoryItemID) ?? {
        name: pick.name,
        count: 0,
      };
      row.count += 1;
      quantityCounts.set(pick.inventoryItemID, row);
    }
    for (const [itemId, row] of quantityCounts) {
      lines.push({
        inventoryItemID: itemId,
        name: row.name,
        equipmentAssetID: 0,
        quantity: row.count,
      });
    }

    const branch = this.branches().find((row) => row.id === branchId);
    const location = this.locations().find((row) => row.id === locationId);

    const sendNext = (index: number) => {
      if (index >= lines.length) {
        this.closeReservationModal();
        this.showToastMessage(
          lines.length > 1
            ? `${lines.length} items reserved.`
            : this.formStatus() === 'completed'
              ? 'Reservation completed. Equipment is paid and waiting on Check-Out.'
              : 'Reservation created successfully',
          'success',
        );
        return;
      }

      const line = lines[index];
      this.equipmentService
        .createReservationViaApi({
          customer,
          equipment: line.name,
          branch: branch?.name ?? branchId,
          branchId,
          location: location?.name ?? locationId,
          locationId,
          storeLocationID: Number(locationId) || 0,
          startDate,
          endDate,
          status: this.formStatus(),
          quantityRequested: line.quantity,
          inventoryItemID: line.inventoryItemID,
          equipmentAssetID: line.equipmentAssetID,
          branchID: Number(branchId) || 0,
        })
        .subscribe((result) => {
          if (!result.success) {
            this.showToastMessage(
              result.message ?? `${line.name} is not available to reserve.`,
              'error',
            );
            return;
          }
          sendNext(index + 1);
        });
    };

    this.inventory.loadItemMasterAndStock().subscribe(() => sendNext(0));
  }

  deleteReservation(reservation: ReservationListRow): void {
    if (!this.auth.hasPermission('delete_orders')) {
      this.showToastMessage('You do not have permission to delete reservations.', 'error');
      return;
    }
    if (this.reservationIsIssued(reservation)) {
      this.showToastMessage(
        'This item has been issued. The reservation status cannot be changed.',
        'error',
      );
      return;
    }
    const qty = reservation.quantityRequested ?? 1;
    if (
      !confirmAction(
        reservation.status === 'completed'
          ? `Cancel this completed reservation for ${qty} ${reservation.equipment}? This removes it from Check-Out (paid waiting) and sets it back to Pending.`
          : `Cancel this reservation for ${qty} ${reservation.equipment}?`,
      )
    ) {
      return;
    }

    const members = this.reservationMembers(reservation);
    forkJoin(
      members.map((member) =>
        this.equipmentService.deleteReservationViaApi(member.id),
      ),
    ).subscribe((results) => {
        const result = results.find((row) => !row.success) ?? results[0];
        if (result?.success) {
          this.showToastMessage(
            reservation.status === 'completed'
              ? 'Waiting checkout removed. Stock is available on Current Stock again.'
              : 'Reservation cancelled. Stock returned to Current Stock.',
            'success',
          );
        } else {
          this.showToastMessage(
            result?.message ?? 'Failed to cancel reservation',
            'error',
          );
        }
      });
  }

  readonly toastType = signal<'success' | 'error' | 'info'>('success');

  showToastMessage(
    message: string,
    type: 'success' | 'error' | 'info' = 'success',
  ): void {
    this.toastMessage.set(message);
    this.toastType.set(type);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  showSuccessToast(message: string): void {
    this.showToastMessage(message, 'success');
  }

  closeToast(): void {
    this.showToast.set(false);
  }

  historyOutcomeLabel(outcome: ReservationHistoryRow['outcome']): string {
    switch (outcome) {
      case 'checked-out':
        return 'Checked out';
      case 'returned':
        return 'Returned';
      case 'cancelled':
        return 'Cancelled';
    }
  }

  exportData(): void {
    const history = this.listView() === 'history';
    const rows = history
      ? this.historyReservations()
      : this.filteredReservations();
    if (!rows.length) {
      this.showToastMessage(
        history
          ? 'No reservation history matches the current filters.'
          : 'No reservations match the current filters.',
        'info',
      );
      return;
    }

    this.exporter.downloadCsv(
      `${history ? 'reservation-history' : 'reservations'}-${new Date().toISOString().slice(0, 10)}`,
      rows.map((item) => ({
        'Reservation ID': item.reservationNumber,
        Customer: item.customer,
        Equipment: item.equipment,
        Quantity: item.quantityRequested ?? 1,
        Branch: item.branch,
        Location: item.location,
        'Start Date': item.startDate,
        'End Date': item.endDate,
        Status: history
          ? this.historyOutcomeLabel(
              (item as ReservationHistoryRow).outcome,
            )
          : item.status,
        ...(history
          ? { History: (item as ReservationHistoryRow).events.join(' → ') }
          : {}),
      })),
    );
  }
}
